import { useCallback, useEffect, useState } from "react";
import { Download, Plus, Search, Upload } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Loader } from "@/components/ui/Loader";
import {
  csvColaboradoresUrl,
  downloadGgCsv,
  extractGgError,
  fetchColaboradores,
  fetchSetores,
  inativarColaboradorApi,
  type GgColaboradorDto,
} from "@/services/genteGestao";
import { ModalColaboradorGg } from "./ModalColaboradorGg";
import { ModalImportarColaboradores } from "./ModalImportarColaboradores";

export function GenteGestaoColaboradoresPage() {
  const [q, setQ] = useState("");
  const [setor, setSetor] = useState("");
  const [ativo, setAtivo] = useState("true");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<GgColaboradorDto[]>([]);
  const [setores, setSetores] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<GgColaboradorDto | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [toInactivate, setToInactivate] = useState<GgColaboradorDto | null>(null);
  const pageSize = 20;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, setoresList] = await Promise.all([
        fetchColaboradores({ q, setor, ativo, page, pageSize }),
        fetchSetores(),
      ]);
      setItems(list.colaboradores);
      setTotal(list.total);
      setSetores(setoresList);
    } catch (e) {
      setError(extractGgError(e, "Não foi possível carregar os colaboradores."));
    } finally {
      setLoading(false);
    }
  }, [q, setor, ativo, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-card">
        <div className="min-w-[180px] flex-1">
          <Input
            label="Busca"
            placeholder="Nome ou usuário"
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="w-44">
          <Select
            label="Setor"
            value={setor}
            onChange={(e) => {
              setPage(1);
              setSetor(e.target.value);
            }}
            options={setores.map((s) => ({ value: s, label: s }))}
            placeholder="Todos"
          />
        </div>
        <div className="w-40">
          <Select
            label="Situação"
            value={ativo}
            onChange={(e) => {
              setPage(1);
              setAtivo(e.target.value);
            }}
            options={[
              { value: "true", label: "Ativos" },
              { value: "false", label: "Inativos" },
              { value: "", label: "Todos" },
            ]}
            placeholder="Todos"
          />
        </div>
        <Button
          variant="secondary"
          leftIcon={<Download className="h-4 w-4" />}
          onClick={() => void downloadGgCsv(csvColaboradoresUrl(), "colaboradores.csv")}
        >
          Exportar CSV
        </Button>
        <Button variant="secondary" leftIcon={<Upload className="h-4 w-4" />} onClick={() => setImportOpen(true)}>
          Importar
        </Button>
        <Button
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          Novo
        </Button>
      </div>

      {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-card">
        {loading && items.length === 0 ? (
          <div className="flex justify-center py-16">
            <Loader label="Carregando colaboradores..." />
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-slate-500">Nenhum colaborador encontrado.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Usuário</th>
                <th className="px-4 py-3">Setor</th>
                <th className="px-4 py-3">Cargo</th>
                <th className="px-4 py-3">Líder direto</th>
                <th className="px-4 py-3">Telefone</th>
                <th className="px-4 py-3">Situação</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-800">{c.nome}</td>
                  <td className="px-4 py-2 text-slate-600">{c.usuario}</td>
                  <td className="px-4 py-2 text-slate-600">{c.setor ?? "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{c.cargo ?? "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{c.liderNome ?? "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{c.telefone ?? "—"}</td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        c.ativo
                          ? "rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"
                          : "rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500"
                      }
                    >
                      {c.ativo ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="mr-2 text-xs font-semibold text-brand-700 hover:underline"
                      onClick={() => {
                        setEditing(c);
                        setModalOpen(true);
                      }}
                    >
                      Editar
                    </button>
                    {c.ativo && (
                      <button
                        className="text-xs font-semibold text-red-600 hover:underline"
                        onClick={() => setToInactivate(c)}
                      >
                        Inativar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>
          {total} registro(s) · página {page} de {pages}
        </span>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Próxima
          </Button>
        </div>
      </div>

      <ModalColaboradorGg
        open={modalOpen}
        colaborador={editing}
        onClose={() => setModalOpen(false)}
        onSaved={() => void load()}
      />
      <ModalImportarColaboradores open={importOpen} onClose={() => setImportOpen(false)} onImported={() => void load()} />
      <ConfirmModal
        isOpen={Boolean(toInactivate)}
        title="Inativar colaborador"
        message={`Inativar ${toInactivate?.nome}? O cadastro permanece para o histórico das pesquisas.`}
        confirmLabel="Inativar"
        onCancel={() => setToInactivate(null)}
        onConfirm={async () => {
          if (!toInactivate) return;
          await inativarColaboradorApi(toInactivate.id);
          setToInactivate(null);
          await load();
        }}
      />
    </div>
  );
}
